import React, { useState, useEffect } from 'react';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../../../hooks/useAuth';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { updateTask, addComment, addDependency, removeDependency } from '../services/firestore';
import { FiX, FiCheck, FiCalendar, FiUser, FiTag, FiAlignLeft, FiList, FiActivity, FiSidebar, FiMaximize, FiLayout, FiChevronDown, FiZap, FiLink, FiPlus, FiTrash2, FiRepeat, FiThumbsUp, FiThumbsDown, FiSend, FiClock, FiPlay, FiPause } from 'react-icons/fi';
import toast from 'react-hot-toast';
import HxSelect from '../../../components/ui/HxSelect';
import { UserCheck as LUserCheck } from 'lucide-react';
import CollaboratorSelector from './CollaboratorSelector';
import SubtaskTable from './SubtaskTable';
import DatePickerPopover from './DatePickerPopover';
import RichTextEditor from './RichTextEditor';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import AIPopover from './AIPopover';

const evaluateFormula = (formula, fields) => {
  try {
    let expr = formula;
    fields.forEach(f => {
      const regex = new RegExp(`\\{${f.nombre}\\}`, 'gi');
      expr = expr.replace(regex, parseFloat(f.valor) || 0);
    });
    if (/^[\d\s+\-*/().]+$/.test(expr)) {
      return new Function('return ' + expr)();
    }
    return 'Error';
  } catch { return 'Error'; }
};

const CustomFieldsSection = ({ customFields, onSave }) => {
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [newFieldFormula, setNewFieldFormula] = useState('');

  const addField = async () => {
    if (!newFieldName.trim()) return;
    const field = {
      id: `cf-${Date.now()}`,
      nombre: newFieldName.trim(),
      tipo: newFieldType,
      formula: newFieldType === 'formula' ? newFieldFormula : null,
      valor: '',
    };
    await onSave([...customFields, field]);
    setNewFieldName('');
    setNewFieldType('text');
    setNewFieldFormula('');
    setShowAddField(false);
  };

  const updateFieldValue = async (fieldId, newValue) => {
    await onSave(customFields.map(f => f.id === fieldId ? { ...f, valor: newValue } : f));
  };

  const removeField = async (fieldId) => {
    await onSave(customFields.filter(f => f.id !== fieldId));
  };

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1 text-gray-600">
          <FiTag size={12} className="text-indigo-600" />
          <span className="text-[11px] font-bold uppercase tracking-wide">Campos personalizados</span>
          {customFields.length > 0 && (
            <span className="text-[9px] bg-indigo-100 text-indigo-700 px-1 py-0 rounded-full font-bold">{customFields.length}</span>
          )}
        </div>
        <button onClick={() => setShowAddField(!showAddField)} className="text-gray-400 hover:text-indigo-600">
          <FiPlus size={12} />
        </button>
      </div>

      {showAddField && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2 mb-2 space-y-1.5">
          <div className="flex gap-1.5">
            <input
              type="text"
              value={newFieldName}
              onChange={(e) => setNewFieldName(e.target.value)}
              placeholder="Nombre del campo"
              className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
            <select
              value={newFieldType}
              onChange={(e) => setNewFieldType(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            >
              <option value="text">Texto</option>
              <option value="number">Numero</option>
              <option value="formula">Formula</option>
            </select>
          </div>
          {newFieldType === 'formula' && (
            <input
              type="text"
              value={newFieldFormula}
              onChange={(e) => setNewFieldFormula(e.target.value)}
              placeholder="Ej: {precio} * {cantidad}"
              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
          )}
          <div className="flex gap-1">
            <button onClick={addField} className="px-2 py-1 text-[10px] bg-indigo-600 text-white rounded font-medium hover:bg-indigo-700">Agregar</button>
            <button onClick={() => setShowAddField(false)} className="px-2 py-1 text-[10px] text-gray-500 hover:text-gray-700">Cancelar</button>
          </div>
        </div>
      )}

      {customFields.length > 0 && (
        <div className="space-y-1">
          {customFields.map(field => {
            const isFormula = field.tipo === 'formula';
            const computedValue = isFormula ? evaluateFormula(field.formula, customFields) : field.valor;

            return (
              <div key={field.id} className="flex items-center gap-2 group">
                <span className="text-[10px] text-gray-500 w-24 truncate shrink-0" title={field.nombre}>{field.nombre}</span>
                {isFormula ? (
                  <span className="flex-1 px-2 py-1 text-xs bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 rounded font-mono" title={`Formula: ${field.formula}`}>
                    = {computedValue}
                  </span>
                ) : (
                  <input
                    type={field.tipo === 'number' ? 'number' : 'text'}
                    value={field.valor || ''}
                    onChange={(e) => updateFieldValue(field.id, e.target.value)}
                    className="flex-1 px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  />
                )}
                <button onClick={() => removeField(field.id)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500">
                  <FiTrash2 size={10} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DependencySection = ({ task, allTasks, user, projectId, folderId, listId }) => {
  const [showAddDep, setShowAddDep] = useState(false);
  const [depType, setDepType] = useState('blocked_by');
  const [searchQuery, setSearchQuery] = useState('');

  if (!task) return null;

  const dependencies = task.dependencies || [];
  const blockedBy = dependencies.filter(d => d.type === 'blocked_by');
  const blocking = dependencies.filter(d => d.type === 'blocking');

  const availableTasks = allTasks.filter(t =>
    t.id !== task.id &&
    !dependencies.some(d => d.taskId === t.id) &&
    (searchQuery === '' || t.titulo.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleAdd = async (targetTaskId) => {
    try {
      await addDependency(user.uid, projectId, folderId, listId, task.id, {
        taskId: targetTaskId,
        type: depType,
      });
      // Also add the reverse dependency on the other task
      const reverseType = depType === 'blocked_by' ? 'blocking' : 'blocked_by';
      await addDependency(user.uid, projectId, folderId, listId, targetTaskId, {
        taskId: task.id,
        type: reverseType,
      });
      setSearchQuery('');
      setShowAddDep(false);
    } catch {
      toast.error('Error al agregar dependencia');
    }
  };

  const handleRemove = async (dep) => {
    try {
      await removeDependency(user.uid, projectId, folderId, listId, task.id, dep);
      const reverseType = dep.type === 'blocked_by' ? 'blocking' : 'blocked_by';
      await removeDependency(user.uid, projectId, folderId, listId, dep.taskId, {
        taskId: task.id,
        type: reverseType,
      });
    } catch {
      toast.error('Error al eliminar dependencia');
    }
  };

  const getTaskTitle = (taskId) => {
    const t = allTasks.find(x => x.id === taskId);
    return t ? t.titulo : 'Tarea eliminada';
  };

  const getTaskStatus = (taskId) => {
    const t = allTasks.find(x => x.id === taskId);
    return t?.estado || 'planificacion';
  };

  const statusColors = {
    'planificacion': 'bg-gray-300',
    'en-curso': 'bg-blue-500',
    'finalizado': 'bg-green-500',
  };

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1 text-gray-600">
          <FiLink size={12} className="text-orange-500" />
          <span className="text-[11px] font-bold uppercase tracking-wide">Dependencias</span>
          {dependencies.length > 0 && (
            <span className="text-[9px] bg-orange-100 text-orange-700 px-1 py-0 rounded-full font-bold">{dependencies.length}</span>
          )}
        </div>
        <button
          onClick={() => setShowAddDep(!showAddDep)}
          className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FiPlus size={12} />
        </button>
      </div>

      {/* Existing Dependencies */}
      {blockedBy.length > 0 && (
        <div className="mb-1.5">
          <span className="text-[10px] text-red-500 font-semibold uppercase">Bloqueada por</span>
          {blockedBy.map((dep, i) => (
            <div key={i} className="flex items-center gap-2 py-1 px-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 group">
              <div className={`w-2 h-2 rounded-full ${statusColors[getTaskStatus(dep.taskId)]}`} />
              <span className="text-xs text-gray-700 dark:text-gray-300 flex-1 truncate">{getTaskTitle(dep.taskId)}</span>
              <button onClick={() => handleRemove(dep)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all">
                <FiTrash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {blocking.length > 0 && (
        <div className="mb-1.5">
          <span className="text-[10px] text-amber-600 font-semibold uppercase">Bloquea a</span>
          {blocking.map((dep, i) => (
            <div key={i} className="flex items-center gap-2 py-1 px-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 group">
              <div className={`w-2 h-2 rounded-full ${statusColors[getTaskStatus(dep.taskId)]}`} />
              <span className="text-xs text-gray-700 dark:text-gray-300 flex-1 truncate">{getTaskTitle(dep.taskId)}</span>
              <button onClick={() => handleRemove(dep)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all">
                <FiTrash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {dependencies.length === 0 && !showAddDep && (
        <p className="text-[11px] text-gray-400 italic">Sin dependencias</p>
      )}

      {/* Add Dependency Form */}
      {showAddDep && (
        <div className="mt-1 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex gap-1 mb-2">
            <button
              onClick={() => setDepType('blocked_by')}
              className={`px-2 py-1 text-[10px] font-medium rounded ${depType === 'blocked_by' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
            >
              Bloqueada por
            </button>
            <button
              onClick={() => setDepType('blocking')}
              className={`px-2 py-1 text-[10px] font-medium rounded ${depType === 'blocking' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
            >
              Bloquea a
            </button>
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar tarea..."
            className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-400 mb-1"
            autoFocus
          />
          <div className="max-h-[120px] overflow-y-auto">
            {availableTasks.slice(0, 8).map(t => (
              <button
                key={t.id}
                onClick={() => handleAdd(t.id)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded transition-colors text-left"
              >
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColors[t.estado || 'planificacion']}`} />
                <span className="truncate">{t.titulo}</span>
              </button>
            ))}
            {availableTasks.length === 0 && (
              <p className="text-[11px] text-gray-400 py-2 text-center">No hay tareas disponibles</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TaskDrawer = () => {
  const [approvalPick, setApprovalPick] = useState(''); // aprobador elegido para pedir aprobación
  const { user } = useAuth();
  const { selectedTaskId, tasks, myTasks, showTaskDrawer, setShowTaskDrawer, selectedProjectId, selectedFolderId, selectedListId, selectedSubtaskPath, setSelectedSubtaskPath, users } = useTask();
  // Look for the task in list-specific tasks first, then in cross-project myTasks
  const selectedTask = tasks.find(t => t.id === selectedTaskId) || myTasks.find(t => t.id === selectedTaskId);

  // Use the task's own location IDs (stored in the document) for all operations,
  // falling back to context's navigation IDs for backward compatibility
  const taskProjectId = selectedTask?.projectId || selectedProjectId;
  const taskFolderId = selectedTask?.folderId || selectedFolderId;
  const taskListId = selectedTask?.listId || selectedListId;

  // Función para obtener la subtarea activa según el path
  const getActiveSubtask = () => {
    if (!selectedTask || !selectedSubtaskPath || selectedSubtaskPath.length === 0) {
      return selectedTask;
    }

    let current = selectedTask;
    for (const subtaskId of selectedSubtaskPath) {
      if (!current.subtareas) return selectedTask;
      current = current.subtareas.find(st => st.id === subtaskId);
      if (!current) return selectedTask;
    }
    return current;
  };

  const activeItem = getActiveSubtask();

  // Función para generar breadcrumbs
  const getBreadcrumbs = () => {
    if (!selectedTask) return [];
    const breadcrumbs = [{ id: selectedTask.id, titulo: selectedTask.titulo, path: [] }];

    if (selectedSubtaskPath && selectedSubtaskPath.length > 0) {
      let current = selectedTask;
      const pathSoFar = [];

      for (const subtaskId of selectedSubtaskPath) {
        if (!current.subtareas) break;
        const subtask = current.subtareas.find(st => st.id === subtaskId);
        if (!subtask) break;

        pathSoFar.push(subtaskId);
        breadcrumbs.push({ id: subtask.id, titulo: subtask.titulo, path: [...pathSoFar] });
        current = subtask;
      }
    }

    return breadcrumbs;
  };

  // Función para obtener el nombre de un usuario
  const getUserName = (userId) => {
    // Si es el usuario actual
    if (user && user.uid === userId) {
      return user.displayName || user.email;
    }

    // Buscar en la lista de usuarios
    const foundUser = users?.find(u => u.id === userId || u.email === userId);
    if (foundUser) {
      return foundUser.displayName || foundUser.email;
    }

    return 'Usuario';
  };

  // Local state for inputs
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [newCommentText, setNewCommentText] = useState('');

  // View Mode: 'side', 'center', 'full'
  const [viewMode, setViewMode] = useState('side');

  // Drawer width (resizable)
  const [drawerWidth, setDrawerWidth] = useState(800); // Default width in pixels
  const [isResizing, setIsResizing] = useState(false);

  // Date Picker Popover
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerPos, setDatePickerPos] = useState(null);
  const [aiPopover, setAiPopover] = useState(null);

  // Sync local state when task/subtask changes
  useEffect(() => {
    if (activeItem) {
      setTitle(activeItem.titulo || '');
      setDescription(activeItem.descripcion || '');
    }
  }, [activeItem?.id, activeItem?.titulo, activeItem?.descripcion, selectedSubtaskPath]);

  // Handle drawer resizing
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = window.innerWidth - e.clientX;
      // Min width: 400px, Max width: 90% of screen
      const minWidth = 400;
      const maxWidth = window.innerWidth * 0.9;

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setDrawerWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  if (!showTaskDrawer || !selectedTask) {
    return null;
  }

  const handleClose = () => {
    setShowTaskDrawer(false);
    setSelectedSubtaskPath([]);
  };

  // Auto-save handler - Funciona tanto para tareas como subtareas
  const handleAutoSave = async (field, value) => {
    if (!activeItem || activeItem[field] === value) return; // No change

    try {
      if (selectedSubtaskPath && selectedSubtaskPath.length > 0) {
        // Es una subtarea
        const { updateSubtask } = require('../services/firestore');
        await updateSubtask(user.uid, taskProjectId, taskFolderId, taskListId, selectedTask.id, selectedSubtaskPath, {
          [field]: value
        });
      } else {
        // Es una tarea principal - updateTask now auto-logs activity
        await updateTask(user.uid, taskProjectId, taskFolderId, taskListId, selectedTask.id, {
          [field]: value
        });
      }
    } catch (error) {
      if (error?.message?.includes('exceeds the maximum allowed size')) {
        toast.error('El documento es demasiado grande. Reduce el contenido de la descripción o usa enlaces en lugar de imágenes embebidas.', { duration: 5000 });
      } else {
        toast.error('Error al guardar cambios');
      }
      console.error(error);
    }
  };

  // --- Comment Handlers ---
  const handleAddComment = async () => {
    if (!newCommentText.trim()) return;
    try {
      await addComment(user.uid, taskProjectId, taskFolderId, taskListId, selectedTask.id, {
        mensaje: newCommentText
      });
      setNewCommentText('');
      toast.success('Comentario agregado');
    } catch (error) {
      toast.error('Error al comentar');
    }
  };

  const ESTADO_COLORS = {
    planificacion: 'bg-gray-100 text-gray-700',
    'en-curso': 'bg-blue-100 text-blue-700',
    finalizado: 'bg-green-100 text-green-700'
  };

  // Dynamic classes and styles based on viewMode
  const getContainerClasses = () => {
    switch (viewMode) {
      case 'center':
        return 'w-full max-w-4xl h-[90vh] rounded-xl shadow-2xl m-auto animate-in fade-in zoom-in-95 duration-200';
      case 'full':
        return 'w-full h-full animate-in fade-in duration-200';
      case 'side':
      default:
        return 'h-full shadow-2xl ml-auto animate-slide-in-right';
    }
  };

  const getContainerStyle = () => {
    if (viewMode === 'side') {
      // On mobile, use full width
      const isMobile = window.innerWidth < 768;
      return { width: isMobile ? '100%' : `${drawerWidth}px` };
    }
    return {};
  };

  const getWrapperClasses = () => {
    if (viewMode === 'center') {
      return 'fixed inset-0 z-50 flex items-center justify-center p-4';
    }
    return 'fixed inset-0 z-50 flex justify-end pointer-events-none';
  };

  return (
    <div className={getWrapperClasses()}>
      <div className={`${getContainerClasses()} bg-white flex flex-col overflow-hidden relative pointer-events-auto`} style={getContainerStyle()}>

        {/* Resize Handle - Solo visible en modo 'side' */}
        {viewMode === 'side' && (
          <div
            onMouseDown={() => setIsResizing(true)}
            className={`absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500 transition-colors ${isResizing ? 'bg-blue-500' : 'bg-transparent'
              }`}
            style={{ zIndex: 100 }}
          >
            <div className="absolute left-0 top-0 bottom-0 w-4 -translate-x-1.5" />
          </div>
        )}

        {/* Header - ultra compact */}
        <div className="flex items-center justify-between px-4 py-1.5 border-b border-gray-100 bg-white z-10">
          <div className="flex items-center gap-1.5 text-[11px] min-w-0">
            <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-400 font-mono shrink-0">{selectedTask.id.slice(-6).toUpperCase()}</span>
            <div className="flex items-center gap-1 text-gray-500 min-w-0 truncate">
              {getBreadcrumbs().map((crumb, index) => (
                <React.Fragment key={crumb.id}>
                  {index > 0 && <span className="text-gray-300">/</span>}
                  <button
                    type="button"
                    onClick={() => index === 0 ? setSelectedSubtaskPath([]) : setSelectedSubtaskPath(crumb.path)}
                    className={`hover:text-blue-600 truncate max-w-[120px] ${index === getBreadcrumbs().length - 1 ? 'font-medium text-gray-700' : ''}`}
                    title={crumb.titulo}
                  >
                    {crumb.titulo}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <div className="flex bg-gray-100 rounded p-0.5 mr-1">
              <button type="button" onClick={() => setViewMode('side')} className={`p-1 rounded ${viewMode === 'side' ? 'bg-white shadow text-blue-600' : 'text-gray-400'}`}><FiSidebar size={12} /></button>
              <button type="button" onClick={() => setViewMode('center')} className={`p-1 rounded ${viewMode === 'center' ? 'bg-white shadow text-blue-600' : 'text-gray-400'}`}><FiLayout size={12} /></button>
              <button type="button" onClick={() => setViewMode('full')} className={`p-1 rounded ${viewMode === 'full' ? 'bg-white shadow text-blue-600' : 'text-gray-400'}`}><FiMaximize size={12} /></button>
            </div>
            <button onClick={() => setShowTaskDrawer(false)} className="p-1 hover:bg-gray-100 rounded text-gray-400"><FiX size={16} /></button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto px-4 pt-2 pb-3">

            {/* Title - inline editable, border on focus */}
            <div className="mb-1.5 relative group flex items-start gap-1.5">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => handleAutoSave('titulo', title)}
                placeholder="Título de la tarea"
                className="w-full text-lg font-bold text-gray-900 border-none focus:ring-0 focus:border-b focus:border-blue-400 p-0 pb-0.5 placeholder-gray-300 bg-transparent leading-snug hover:border-b hover:border-gray-200 transition-colors cursor-text"
                style={{ borderBottom: '1px solid transparent' }}
                onFocus={(e) => e.target.style.borderBottom = '1px solid #3b82f6'}
                onBlurCapture={(e) => e.target.style.borderBottom = '1px solid transparent'}
              />
              <button
                onClick={(e) => {
                  if (!title.trim()) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  setAiPopover({ target: 'title', position: { top: rect.bottom + 10, left: rect.left }, initialContent: title });
                }}
                className="mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100 shrink-0"
                title="Mejorar redacción con IA"
              >
                <FiZap size={12} />
              </button>
            </div>

            {aiPopover && (
              <AIPopover
                initialContent={aiPopover.initialContent}
                position={aiPopover.position}
                targetName={aiPopover.target === 'title' ? 'título' : 'descripción'}
                onClose={() => setAiPopover(null)}
                onInsert={(newContent) => {
                  if (aiPopover.target === 'title') { setTitle(newContent); handleAutoSave('titulo', newContent); }
                }}
              />
            )}

            {/* Properties - ultra compact */}
            <div className="flex flex-wrap items-center gap-1.5 mb-2 pb-2 border-b border-gray-100">
              {/* Asignado */}
              <div className="flex items-center gap-1.5 group/prop relative">
                <div className="hover:bg-gray-100 rounded-full transition-colors cursor-pointer">
                  <div className="w-6 h-6 rounded-full bg-white border border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:border-gray-400">
                    <CollaboratorSelector
                      task={activeItem}
                      onChange={(newAsignados) => handleAutoSave('asignados', newAsignados)}
                    />
                  </div>
                </div>
                <span className="text-[11px] text-gray-400 opacity-0 group-hover/prop:opacity-100 transition-opacity">Responsable</span>
              </div>

              <div className="w-px h-4 bg-gray-200" />

              {/* Fecha */}
              <div className="relative group/prop">
                <div
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setDatePickerPos({ top: rect.bottom + 5, left: rect.left });
                    setShowDatePicker(true);
                  }}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium cursor-pointer transition-all ${activeItem?.fechaVencimiento
                    ? 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    : 'text-gray-400 hover:bg-gray-50'
                    }`}
                >
                  <FiCalendar size={11} />
                  {activeItem?.fechaVencimiento ? (
                    format(
                      typeof activeItem.fechaVencimiento === 'object' && activeItem.fechaVencimiento.seconds
                        ? new Date(activeItem.fechaVencimiento.seconds * 1000)
                        : new Date(activeItem.fechaVencimiento),
                      "d MMM",
                      { locale: es }
                    )
                  ) : (
                    <span>Fecha</span>
                  )}
                </div>
                {showDatePicker && datePickerPos && (
                  <DatePickerPopover
                    date={activeItem?.fechaVencimiento}
                    onDateChange={(newDate) => {
                      handleAutoSave('fechaVencimiento', newDate);
                      setShowDatePicker(false);
                    }}
                    position={datePickerPos}
                    onClose={() => setShowDatePicker(false)}
                  />
                )}
              </div>

              {/* Estado */}
              <div className="relative">
                <select
                  value={activeItem?.estado || 'planificacion'}
                  onChange={(e) => handleAutoSave('estado', e.target.value)}
                  className={`appearance-none pl-2 pr-6 py-0.5 rounded text-[11px] font-medium cursor-pointer focus:outline-none focus:ring-1 focus:ring-offset-1 transition-all border-none ${activeItem?.estado === 'finalizado' ? 'bg-green-100 text-green-700 focus:ring-green-400' :
                    activeItem?.estado === 'en-curso' ? 'bg-blue-100 text-blue-700 focus:ring-blue-400' :
                      'bg-gray-100 text-gray-600 focus:ring-gray-300'
                    }`}
                >
                  <option value="planificacion">Planificación</option>
                  <option value="en-curso">En Curso</option>
                  <option value="finalizado">Finalizado</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1.5 text-current opacity-60">
                  <FiChevronDown size={10} />
                </div>
              </div>

              {/* Prioridad */}
              <div className="relative">
                <select
                  value={activeItem?.prioridad || 'media'}
                  onChange={(e) => handleAutoSave('prioridad', e.target.value)}
                  className={`appearance-none pl-2 pr-6 py-0.5 rounded text-[11px] font-medium cursor-pointer focus:outline-none focus:ring-1 focus:ring-offset-1 transition-all border border-transparent ${activeItem?.prioridad === 'alta' ? 'bg-red-50 text-red-600 focus:ring-red-400' :
                    activeItem?.prioridad === 'baja' ? 'bg-blue-50 text-blue-600 focus:ring-blue-400' :
                      'bg-gray-50 text-gray-600 focus:ring-gray-400'
                    }`}
                >
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1.5 text-current opacity-60">
                  <FiTag size={9} />
                </div>
              </div>

              {/* Milestone toggle */}
              {selectedSubtaskPath.length === 0 && (
                <button
                  onClick={() => handleAutoSave('isMilestone', !selectedTask.isMilestone)}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                    selectedTask.isMilestone
                      ? 'bg-amber-100 text-amber-700'
                      : 'text-gray-400 hover:bg-gray-50'
                  }`}
                  title="Marcar como hito"
                >
                  <span className="text-[13px]">◆</span>
                  <span className="hidden sm:inline">Hito</span>
                </button>
              )}
            </div>

            {/* Compact metadata: Tiempo | Recurrencia + Aprobación side by side */}
            {selectedSubtaskPath.length === 0 && (
              <div className="mb-2 flex flex-wrap items-center gap-1.5">
                {/* Timer chip */}
                {(() => {
                  const timeEntries = selectedTask.timeEntries || [];
                  const activeEntry = timeEntries.find(e => !e.end && e.userId === user.uid);
                  const totalMs = timeEntries.reduce((sum, e) => {
                    if (!e.end) return sum + (Date.now() - new Date(e.start).getTime());
                    return sum + (new Date(e.end).getTime() - new Date(e.start).getTime());
                  }, 0);
                  const fmtDur = (ms) => {
                    const s = Math.floor(ms / 1000); const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60);
                    if (h > 0) return `${h}h${m}m`; if (m > 0) return `${m}m`; return `${s % 60}s`;
                  };
                  const handleStart = async () => {
                    try {
                      await updateTask(user.uid, taskProjectId, taskFolderId, taskListId, selectedTask.id, {
                        timeEntries: [...timeEntries, { id: `te-${Date.now()}`, userId: user.uid, userEmail: user.email, start: new Date().toISOString(), end: null }]
                      });
                    } catch { toast.error('Error'); }
                  };
                  const handleStop = async () => {
                    if (!activeEntry) return;
                    try {
                      await updateTask(user.uid, taskProjectId, taskFolderId, taskListId, selectedTask.id, {
                        timeEntries: timeEntries.map(e => e.id === activeEntry.id ? { ...e, end: new Date().toISOString() } : e)
                      });
                      toast.success('Tiempo registrado');
                    } catch { toast.error('Error'); }
                  };
                  return activeEntry ? (
                    <button onClick={handleStop} className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-red-500 text-white rounded-full hover:bg-red-600 animate-pulse">
                      <FiPause size={9} /> {fmtDur(Date.now() - new Date(activeEntry.start).getTime())}
                    </button>
                  ) : (
                    <button onClick={handleStart} className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded-full hover:bg-indigo-50 hover:text-indigo-600 transition-colors">
                      <FiPlay size={9} /> {totalMs > 0 ? fmtDur(totalMs) : 'Timer'}
                    </button>
                  );
                })()}

                {/* Recurrence chip */}
                <div className="relative">
                  <select
                    value={selectedTask.recurrence || 'none'}
                    onChange={(e) => handleAutoSave('recurrence', e.target.value === 'none' ? null : e.target.value)}
                    className={`appearance-none pl-1.5 pr-4 py-0.5 rounded-full text-[10px] font-medium cursor-pointer focus:outline-none transition-all border ${
                      selectedTask.recurrence && selectedTask.recurrence !== 'none'
                        ? 'bg-teal-50 border-teal-200 text-teal-700'
                        : 'bg-gray-100 border-transparent text-gray-500'
                    }`}
                  >
                    <option value="none">🔁 Sin repetir</option>
                    <option value="daily">🔁 Diaria</option>
                    <option value="weekdays">🔁 L-V</option>
                    <option value="weekly">🔁 Semanal</option>
                    <option value="biweekly">🔁 Quincenal</option>
                    <option value="monthly">🔁 Mensual</option>
                    <option value="quarterly">🔁 Trimestral</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1 text-gray-400">
                    <FiChevronDown size={8} />
                  </div>
                </div>

                {/* Approval chip */}
                {!selectedTask.approval ? (
                  <>
                    <HxSelect
                      size="sm"
                      minWidth={140}
                      popWidth={280}
                      value={approvalPick}
                      onChange={setApprovalPick}
                      options={(users || []).filter(u => u.email !== user.email).slice().sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '')).map(u => ({
                        value: u.email, label: u.displayName || u.email, sub: u.displayName ? u.email : undefined, photo: u.photoURL || undefined, color: '#0e9f6e',
                      }))}
                      placeholder="Aprobador"
                      searchPlaceholder="Buscar aprobador…"
                      icon={LUserCheck}
                      ariaLabel="Aprobador"
                    />
                    <button
                      onClick={async () => {
                        const approverEmail = approvalPick;
                        if (!approverEmail) { toast.error('Selecciona un aprobador'); return; }
                        try {
                          await updateTask(user.uid, taskProjectId, taskFolderId, taskListId, selectedTask.id, {
                            approval: { status: 'pending', approverEmail, requestedBy: user.email, requestedAt: new Date().toISOString(), respondedAt: null }
                          });
                          toast.success('Solicitud enviada');
                        } catch { toast.error('Error'); }
                      }}
                      className="flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 rounded-full hover:bg-emerald-100 transition-colors"
                    >
                      <FiSend size={9} /> Pedir
                    </button>
                  </>
                ) : (
                  <>
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      selectedTask.approval.status === 'approved' ? 'bg-green-100 text-green-700' :
                      selectedTask.approval.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {selectedTask.approval.status === 'approved' ? '✓ Aprobada' :
                       selectedTask.approval.status === 'rejected' ? '✗ Rechazada' : '⏳ Pendiente'}
                    </span>
                    {selectedTask.approval.status === 'pending' && user.email === selectedTask.approval.approverEmail && (
                      <>
                        <button
                          onClick={async () => { try { await updateTask(user.uid, taskProjectId, taskFolderId, taskListId, selectedTask.id, { approval: { ...selectedTask.approval, status: 'approved', respondedAt: new Date().toISOString() } }); toast.success('Aprobada'); } catch { toast.error('Error'); } }}
                          className="px-2 py-0.5 text-[10px] font-medium bg-green-500 text-white rounded-full hover:bg-green-600"
                        >✓</button>
                        <button
                          onClick={async () => { try { await updateTask(user.uid, taskProjectId, taskFolderId, taskListId, selectedTask.id, { approval: { ...selectedTask.approval, status: 'rejected', respondedAt: new Date().toISOString() } }); toast.success('Rechazada'); } catch { toast.error('Error'); } }}
                          className="px-2 py-0.5 text-[10px] font-medium bg-red-500 text-white rounded-full hover:bg-red-600"
                        >✗</button>
                      </>
                    )}
                    {selectedTask.approval.status !== 'pending' && (
                      <button
                        onClick={async () => { try { await updateTask(user.uid, taskProjectId, taskFolderId, taskListId, selectedTask.id, { approval: null }); toast.success('Reiniciado'); } catch { toast.error('Error'); } }}
                        className="text-[9px] text-gray-400 hover:text-gray-600 underline"
                      >Reiniciar</button>
                    )}
                  </>
                )}

                {/* Custom fields toggle */}
                {(activeItem?.camposPersonalizados || []).length > 0 && (
                  <span className="flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-600">
                    <FiTag size={9} /> {(activeItem?.camposPersonalizados || []).length}
                  </span>
                )}
              </div>
            )}

            {/* Custom Fields - collapsible */}
            <CustomFieldsSection
              customFields={activeItem?.camposPersonalizados || []}
              onSave={(updated) => handleAutoSave('camposPersonalizados', updated)}
            />

            {/* Subtasks */}
            <div className="mb-3">
              <div className="flex items-center gap-1 text-gray-600 mb-1">
                <FiList size={12} className="text-purple-600" />
                <span className="text-[11px] font-bold uppercase tracking-wide">Subtareas</span>
                {activeItem?.subtareas && activeItem.subtareas.length > 0 && (
                  <span className="text-[9px] bg-purple-100 text-purple-700 px-1 py-0 rounded-full font-bold">{activeItem.subtareas.length}</span>
                )}
              </div>
              <SubtaskTable
                rootTask={selectedTask}
                activeItem={activeItem}
                currentPath={selectedSubtaskPath || []}
              />
            </div>

            {/* Dependencies */}
            <DependencySection
              task={selectedTask}
              allTasks={tasks}
              user={user}
              projectId={taskProjectId}
              folderId={taskFolderId}
              listId={taskListId}
            />

            {/* Description */}
            <div className="mb-3">
              <div className="flex items-center gap-1 text-gray-600 mb-1">
                <FiAlignLeft size={12} className="text-blue-600" />
                <span className="text-[11px] font-bold uppercase tracking-wide">Descripción</span>
              </div>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                onBlur={() => handleAutoSave('descripcion', description)}
                placeholder="Agregar una descripción más detallada..."
              />
            </div>

            {/* Activity */}
            <div className="mb-3">
              <div className="flex items-center gap-1 text-gray-400 mb-1">
                <FiActivity size={12} />
                <span className="text-[11px] font-semibold">Actividad</span>
                {(() => {
                  const actCount = (selectedTask.actividad?.length || 0) + (selectedTask.comentarios?.length || 0);
                  return actCount > 0 ? <span className="text-[9px] bg-gray-100 text-gray-500 px-1 rounded-full font-bold">{actCount}</span> : null;
                })()}
              </div>
              <div className="space-y-1.5 pl-0.5 max-h-[300px] overflow-y-auto">
                {/* Merge activities + comments into single timeline, sorted by date desc */}
                {(() => {
                  const timeline = [];
                  // Creation event
                  timeline.push({
                    type: 'created',
                    date: selectedTask.fechaCreacion?.seconds
                      ? new Date(selectedTask.fechaCreacion.seconds * 1000)
                      : new Date(),
                    userId: selectedTask.creadoPor || user.uid,
                  });
                  // Activity entries
                  (selectedTask.actividad || []).forEach(act => {
                    timeline.push({
                      type: 'activity',
                      date: new Date(act.fecha),
                      userId: act.usuarioId,
                      campo: act.campo,
                      details: act.detalles,
                      id: act.id,
                    });
                  });
                  // Comments
                  (selectedTask.comentarios || []).forEach(comment => {
                    timeline.push({
                      type: 'comment',
                      date: comment.fecha ? new Date(comment.fecha) : new Date(),
                      userId: comment.usuarioId,
                      message: comment.mensaje,
                      id: comment.id,
                    });
                  });
                  // Sort newest first
                  timeline.sort((a, b) => b.date - a.date);

                  const formatTimeAgo = (date) => {
                    const now = new Date();
                    const diff = Math.floor((now - date) / 1000);
                    if (diff < 60) return 'ahora';
                    if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
                    if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
                    if (diff < 604800) return `hace ${Math.floor(diff / 86400)}d`;
                    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
                  };

                  const activityIcons = {
                    estado: { bg: 'bg-blue-100', dot: 'bg-blue-500' },
                    prioridad: { bg: 'bg-amber-100', dot: 'bg-amber-500' },
                    asignados: { bg: 'bg-purple-100', dot: 'bg-purple-500' },
                    fechaVencimiento: { bg: 'bg-orange-100', dot: 'bg-orange-500' },
                    titulo: { bg: 'bg-gray-100', dot: 'bg-gray-500' },
                    descripcion: { bg: 'bg-teal-100', dot: 'bg-teal-500' },
                    recurrence: { bg: 'bg-cyan-100', dot: 'bg-cyan-500' },
                  };

                  return timeline.map((item, idx) => {
                    if (item.type === 'created') {
                      return (
                        <div key="created" className="flex items-start gap-2">
                          <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          </div>
                          <p className="text-[11px] text-gray-500">
                            <span className="font-medium text-gray-700">{getUserName(item.userId)}</span> creó esta tarea
                            <span className="text-gray-400 ml-1">{formatTimeAgo(item.date)}</span>
                          </p>
                        </div>
                      );
                    }
                    if (item.type === 'comment') {
                      return (
                        <div key={item.id || idx} className="flex items-start gap-2">
                          <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                          </div>
                          <div className="bg-gray-50 px-2 py-1 rounded border border-gray-100 flex-1 min-w-0">
                            <div className="flex justify-between items-center">
                              <span className="text-[10px] font-semibold text-gray-700">{getUserName(item.userId)}</span>
                              <span className="text-[9px] text-gray-400">{formatTimeAgo(item.date)}</span>
                            </div>
                            <p className="text-[10px] text-gray-600">{item.message}</p>
                          </div>
                        </div>
                      );
                    }
                    // activity
                    const iconStyle = activityIcons[item.campo] || { bg: 'bg-gray-100', dot: 'bg-gray-400' };
                    return (
                      <div key={item.id || idx} className="flex items-start gap-2">
                        <div className={`w-4 h-4 rounded-full ${iconStyle.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${iconStyle.dot}`} />
                        </div>
                        <p className="text-[11px] text-gray-500">
                          <span className="font-medium text-gray-700">{getUserName(item.userId)}</span>{' '}
                          {item.details}
                          <span className="text-gray-400 ml-1">{formatTimeAgo(item.date)}</span>
                        </p>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

          </div>

          {/* Comment input - fixed bottom */}
          <div className="border-t border-gray-100 bg-gray-50/30 px-3 py-2">
            <div className="flex gap-2 items-center">
              <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                {user.email?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 relative">
                <textarea
                  value={newCommentText}
                  onChange={(e) => setNewCommentText(e.target.value)}
                  placeholder="Escribe un comentario..."
                  className="w-full px-2.5 py-1.5 pr-16 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow resize-none bg-white"
                  rows="1"
                  style={{ minHeight: '32px' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                />
                <button
                  onClick={handleAddComment}
                  disabled={!newCommentText.trim()}
                  className="absolute right-1 bottom-1 top-1 px-2.5 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold hover:bg-blue-200 disabled:opacity-0 disabled:pointer-events-none transition-all"
                >
                  Enviar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDrawer;
